// Stimulus controller for tag selector autocomplete widget (Issue #905)
// Handles tag search, selection, removal, and optional inline creation
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "results", "selectedTags", "hiddenInputs"]
  static values = {
    url: { type: String, default: "/company/tags/autocomplete" },
    name: { type: String, default: "tag_ids" }
  }

  connect() {
    this.debounceTimer = null
    this.selectedTagIds = new Set()

    // Initialize with any pre-selected tags from hidden inputs
    this.initializeSelectedTags()

    // Close dropdown when clicking outside
    this.boundCloseOnClickOutside = this.closeOnClickOutside.bind(this)
    document.addEventListener("click", this.boundCloseOnClickOutside)
  }

  disconnect() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }
    document.removeEventListener("click", this.boundCloseOnClickOutside)
  }

  // Initialize the set of selected tag IDs from existing hidden inputs
  initializeSelectedTags() {
    const container = this.element.querySelector(".selected-tags")
    if (container) {
      const hiddenInputs = container.querySelectorAll("input[type='hidden']")
      hiddenInputs.forEach(input => {
        this.selectedTagIds.add(input.value)
      })
    }
  }

  // Debounced search for tags via autocomplete endpoint
  search(event) {
    const query = event.target.value.trim()

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }

    if (query.length < 2) {
      this.clearResults()
      return
    }

    this.debounceTimer = setTimeout(() => {
      this.fetchResults(query)
    }, 300)
  }

  // Issue #834 - Changed to HTML pattern for Stimulus/Turbo compatibility
  async fetchResults(query) {
    try {
      // Request HTML partial instead of JSON
      const response = await fetch(`${this.urlValue}?query=${encodeURIComponent(query)}`, {
        headers: {
          "Accept": "text/html",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (!response.ok) {
        console.error("[TagSelector] Autocomplete request failed:", response.status)
        return
      }

      // Parse HTML response and render directly
      const html = await response.text()
      this.renderHtmlResults(html)
    } catch (error) {
      console.error("[TagSelector] Error fetching autocomplete results:", error)
    }
  }

  // Issue #834 - Render HTML results from server partial
  renderHtmlResults(html) {
    if (!this.hasResultsTarget) return

    if (!html || html.trim() === "") {
      this.resultsTarget.innerHTML = '<div class="autocomplete-item no-results">No tags found</div>'
      this.resultsTarget.classList.add("show")
      return
    }

    // Insert the HTML directly from the server partial
    this.resultsTarget.innerHTML = html
    this.resultsTarget.classList.add("show")
  }

  renderResults(tags) {
    if (!this.hasResultsTarget) return

    // Filter out already selected tags
    const availableTags = tags.filter(tag => !this.selectedTagIds.has(String(tag.id)))

    if (availableTags.length === 0) {
      const emptyResult = document.createElement("div")
      emptyResult.className = "autocomplete-item no-results"
      emptyResult.textContent = "No tags found"
      this.resultsTarget.replaceChildren(emptyResult)
      this.resultsTarget.classList.add("show")
      return
    }

    const results = availableTags.map(tag => {
      const item = document.createElement("div")
      item.className = "autocomplete-item"
      item.setAttribute("data-action", "click->tag-selector#select")
      item.setAttribute("data-tag-id", tag.id)
      item.setAttribute("data-tag-name", tag.name)
      item.textContent = tag.name
      return item
    })

    this.resultsTarget.replaceChildren(...results)
    this.resultsTarget.classList.add("show")
  }

  clearResults() {
    if (this.hasResultsTarget) {
      this.resultsTarget.innerHTML = ""
      this.resultsTarget.classList.remove("show")
    }
  }

  // Select a tag from autocomplete results
  select(event) {
    const item = event.currentTarget
    const tagId = item.dataset.tagId
    const tagName = item.dataset.tagName

    // Prevent duplicates
    if (this.selectedTagIds.has(tagId)) {
      this.clearResults()
      return
    }

    this.addTag(tagId, tagName)
    this.clearResults()

    // Clear input
    if (this.hasInputTarget) {
      this.inputTarget.value = ""
      this.inputTarget.focus()
    }
  }

  addTag(tagId, tagName) {
    this.selectedTagIds.add(tagId)

    // Create the chip element
    const chip = document.createElement("span")
    chip.className = "tag-chip"
    chip.textContent = tagName

    const hiddenInput = document.createElement("input")
    hiddenInput.setAttribute("type", "hidden")
    hiddenInput.setAttribute("name", `${this.nameValue}[]`)
    hiddenInput.setAttribute("value", tagId)

    const removeButton = document.createElement("button")
    removeButton.setAttribute("type", "button")
    removeButton.className = "remove-tag"
    removeButton.setAttribute("data-action", "tag-selector#remove")
    removeButton.textContent = "×"

    chip.append(hiddenInput, removeButton)

    // Add to selected tags container
    const container = this.element.querySelector(".selected-tags")
    if (container) {
      container.appendChild(chip)
    }
  }

  // Remove a tag chip and its hidden input
  remove(event) {
    event.preventDefault()
    event.stopPropagation()

    const chip = event.currentTarget.closest(".tag-chip")
    if (!chip) return

    const hiddenInput = chip.querySelector("input[type='hidden']")
    if (hiddenInput) {
      this.selectedTagIds.delete(hiddenInput.value)
    }

    chip.remove()
  }

  // Create a new tag inline (if allowed)
  create(event) {
    event.preventDefault()

    if (!this.hasInputTarget) return

    const tagName = this.inputTarget.value.trim()
    if (!tagName) return

    // For inline creation, we'll use a temporary negative ID
    // The server will create the actual tag on form submission
    const tempId = `new_${Date.now()}`

    this.addTag(tempId, tagName)
    this.inputTarget.value = ""
    this.clearResults()
  }

  closeOnClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.clearResults()
    }
  }
}
